import type { PanderrTemplateDefinition } from "../types";

export const PROFESSIONAL_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "professional-modern",
  name: "Professional Modern",
  description:
    "A premium, versatile business website for professional service companies.",
  category: "professional",

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Built around your business",
      heading:
        "Smart solutions. Thoughtful service. Work that moves you forward.",
      body: "A professional experience built around clear communication, trusted expertise, and results that support your next stage of growth.",
      images: [
        {
          src: "/studio/assets/stock/images/nastuh-abootalebi-eHD8Y1Znfpk-unsplash.jpg",
          alt: "Modern professional office workspace",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Start a Conversation",
          href: "/contact/",
          variant: "primary",
        },
        {
          label: "Explore Our Services",
          href: "/services/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "What we do",
      heading: "Expertise designed around your goals.",
      body: "Flexible professional services focused on making complex decisions simpler and helping you move forward with confidence.",
      services: [
        {
          title: "Strategy",
          description:
            "Clear direction grounded in your priorities, opportunities, and long-term goals.",
          href: "/services/",
        },
        {
          title: "Professional Services",
          description:
            "Thoughtful expertise and hands-on support tailored to the work that matters most.",
          href: "/services/",
        },
        {
          title: "Ongoing Support",
          description:
            "A dependable partner for continued guidance, improvement, and growth.",
          href: "/services/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "Why work with us",
      heading: "Professional doesn't have to feel impersonal.",
      body: "We believe the best work starts with listening. Our approach combines expertise with clear communication, practical thinking, and a genuine commitment to getting the details right.",
      images: [
        {
          src: "/studio/assets/stock/images/brooke-cagle-8jp-6SjVibM-unsplash.jpg",
          alt: "Professionals collaborating in a modern workspace",
          role: "about",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Ready when you are",
      heading: "Let's build what's next.",
      body: "Tell us what you're working toward and we'll help you find the right next step.",
    },
  ],

  pages: [
    {
      id: "about",
      name: "About",
      title: "About Us",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About our company",
          heading: "Expertise you can trust. Service that feels personal.",
          body: "We bring thoughtful strategy, professional expertise, and clear communication to every client relationship.",
          images: [
            {
              src: "/studio/assets/stock/images/brooke-cagle-8jp-6SjVibM-unsplash.jpg",
              alt: "Professionals collaborating in a modern workspace",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Start a Conversation",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "about-story",
          type: "about",
          eyebrow: "Our approach",
          heading: "Professional doesn't have to feel impersonal.",
          body: "We believe the best work starts with listening. Our approach combines practical thinking, trusted expertise, and a genuine commitment to understanding what matters most to you.",
          images: [
            {
              src: "/studio/assets/stock/images/nastuh-abootalebi-eHD8Y1Znfpk-unsplash.jpg",
              alt: "Modern professional office environment",
              role: "about",
            },
          ],
        },

        {
          id: "about-trust",
          type: "trust",
          eyebrow: "What you can expect",
          heading: "Clarity, consistency, and a thoughtful partnership.",
          body: "From the first conversation through ongoing support, we focus on keeping the work clear, collaborative, and aligned with your goals.",
        },

        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Let's connect",
          heading: "Looking for the right professional partner?",
          body: "Tell us what you're working toward and we'll help you determine the best next step.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "services",
      name: "Services",
      title: "Our Services",
      slug: "/services/",
      sections: [
        {
          id: "services-hero",
          type: "hero",
          eyebrow: "Our services",
          heading: "Professional expertise built around your goals.",
          body: "Flexible services designed to simplify decisions, strengthen your next move, and support long-term progress.",
          images: [
            {
              src: "/studio/assets/stock/images/austin-distel-wawEfYdpkag-unsplash.jpg",
              alt: "Professional working in a modern business setting",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Start a Conversation",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "services-list",
          type: "services",
          eyebrow: "What we do",
          heading: "Expertise designed around your priorities.",
          body: "Edit these service areas in Studio to reflect the specific ways your business helps clients.",
          services: [
            {
              title: "Strategy",
              description:
                "Clear direction grounded in your priorities, opportunities, and long-term goals.",
              href: "/contact/",
            },
            {
              title: "Professional Services",
              description:
                "Thoughtful expertise and hands-on support tailored to the work that matters most.",
              href: "/contact/",
            },
            {
              title: "Ongoing Support",
              description:
                "A dependable partner for continued guidance, improvement, and growth.",
              href: "/contact/",
            },
          ],
        },

        {
          id: "services-process",
          type: "process",
          eyebrow: "How we work",
          heading: "A clear process from conversation to execution.",
          body: "We begin by understanding your goals, define the right approach, and stay engaged through implementation and ongoing support.",
        },

        {
          id: "services-cta",
          type: "cta",
          eyebrow: "Ready to move forward?",
          heading: "Let's talk about what you need.",
          body: "Start a conversation and we'll help you identify the right service or next step.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "our-work",
      name: "Our Work",
      title: "Our Work",
      slug: "/our-work/",
      sections: [
        {
          id: "work-hero",
          type: "hero",
          eyebrow: "Our work",
          heading: "Thoughtful work. Clear outcomes. Lasting value.",
          body: "Use this page to showcase client projects, case studies, engagements, or examples of the results your business helps create.",
          images: [
            {
              src: "/studio/assets/stock/images/sean-pollock-PhYq704ffdA-unsplash.jpg",
              alt: "Professional commercial project",
              role: "hero",
            },
          ],
        },

        {
          id: "work-projects",
          type: "projects",
          eyebrow: "Selected work",
          heading: "Examples of how we help clients move forward.",
          body: "Replace these sample projects in Studio with case studies, featured work, or client success stories.",
          projects: [
            {
              title: "Strategic Engagement",
              description:
                "A focused engagement designed to create clarity, direction, and a practical path forward.",
              image: {
                src: "/studio/assets/stock/images/two people in a meeting holding pens.jpg",
                alt: "Professionals discussing strategy",
                role: "project",
              },
            },
            {
              title: "Client Project",
              description:
                "A collaborative project bringing expertise, planning, and execution together.",
              image: {
                src: "/studio/assets/stock/images/six people in a business meeting.jpg",
                alt: "Business team collaborating in a meeting",
                role: "project",
              },
            },
            {
              title: "Ongoing Partnership",
              description:
                "Continued professional support focused on improvement, growth, and long-term results.",
              image: {
                src: "/studio/assets/stock/images/Commercial RE meeting with view of city.jpg",
                alt: "Professional meeting overlooking a city",
                role: "project",
              },
            },
          ],
        },

        {
          id: "work-cta",
          type: "cta",
          eyebrow: "Have something in mind?",
          heading: "Let's talk about your next project.",
          body: "Tell us what you're working toward and we'll help you determine the right next step.",
          buttons: [
            {
              label: "Start a Conversation",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact Us",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Get in touch",
          heading: "Let's start a conversation.",
          body: "Tell us what you're working on, what you need help with, or what questions you have. We'll help you find the right next step.",
          images: [
            {
              src: "/studio/assets/stock/images/woman working on laptop and coffee.jpeg",
              alt: "Professional working at a laptop",
              role: "hero",
            },
          ],
        },

        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Contact information",
          heading: "We're ready when you are.",
          body: "Professional expertise, thoughtful service, and clear communication.",
          bodyField: "site.identity.description",
          buttons: [
            {
              label: "Call Us",
              href: "#",
              hrefField: "site.contact.phone",
              variant: "primary",
            },
            {
              label: "Email Us",
              href: "#",
              hrefField: "site.contact.email",
              variant: "secondary",
            },
          ],
        },

        {
          id: "contact-cta",
          type: "cta",
          eyebrow: "What's next?",
          heading: "Let's build what's next together.",
          body: "Start the conversation and we'll help you determine the best path forward.",
        },
      ],
    },
  ],
};
